import {useMemo} from "react";
import SentryEchart from "../../../../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../../../../utils/responsive-graph.js";
import MetricDashCard from "../../../../../../../../components/dash-card/metric-dash-card.jsx";

export default function AggressorsTypesOverview({data}) {
    const vw = useGraphViewportWidth();
    const cards = data?.cards;
    const aggressionTypes = data?.aggressionTypes;

    const aggressionTypesChart = useMemo(() => {
        const items = Array.isArray(aggressionTypes)
            ? aggressionTypes.map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
            : [];
        const labelFs = fluidPx(12, 21, vw);
        const valueFs = fluidPx(14, 20, vw);
        const labelWidth = fluidPx(120, 180, vw);
        const categoryGap = labelFs + 12;
        const max = Math.max(...items.map(item => item.value), 1);
        const labels = items.map(item => item.label);
        const values = items.map(item => item.value);

        return {
            grid: {
                top: labelFs,
                left: "6%",
                right: "10%",
                bottom: 4,
                outerBounds: {
                    top: labelFs,
                    left: "6%",
                    right: "10%",
                    bottom: 4,
                },
                outerBoundsContain: "axisLabel",
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: "max-width: 320px; white-space: normal;",
                formatter: params => `${params.name}: ${params.value}`,
            },
            xAxis: {
                type: "value",
                max,
                show: false,
            },
            yAxis: {
                type: "category",
                data: labels,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false},
            },
            series: [
                {
                    type: "bar",
                    itemStyle: {
                        color: {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.28, color: "#0080ED"},
                                {offset: 0.52, color: "#1CEAE6"},
                                {offset: 0.72, color: "#3E98D9"},
                                {offset: 1, color: "#003878"},
                            ],
                        },
                        borderRadius: 20,
                    },
                    label: {
                        show: true,
                        position: [0, -4],
                        align: "left",
                        verticalAlign: "bottom",
                        color: "#FDFCFF",
                        fontSize: labelFs,
                        width: labelWidth,
                        overflow: "truncate",
                        ellipsis: "...",
                        formatter: params => labels[params.dataIndex],
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: "#3C3C4A",
                        borderRadius: 12,
                    },
                    data: values,
                },
                {
                    type: "bar",
                    barCategoryGap: categoryGap,
                    barGap: "-100%",
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: "transparent"},
                    label: {
                        show: true,
                        position: "right",
                        distance: 5,
                        color: "#FDFCFF",
                        fontSize: valueFs,
                        formatter: params => values[params.dataIndex],
                    },
                    data: values.map(() => max),
                },
            ],
        };
    }, [aggressionTypes, vw]);

    return (
        <article className="box-border grid h-full min-h-0 min-w-0 w-full grid-rows-[65%_35%] gap-y-3">
            <section className="min-h-0 min-w-0 h-full overflow-hidden flex">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={aggressionTypesChart}
                    notMerge
                    title="Tipos de agressão"
                    titleClass="mb-0! translate-y-2"
                />
            </section>
            <section className="min-h-0 min-w-0 flex gap-2 justify-center px-4">
                <MetricDashCard
                    diagonal
                    diagonalCounter
                    title="Agressores cadastrados"
                    value={cards?.aggressors ?? 0}
                    width="100%"
                    height={80}
                    dashClassName="min-w-0"
                    pClassName="leading-[1.15]!"
                />
                <MetricDashCard
                    diagonal
                    title="Com mais de um vínculo"
                    value={cards?.aggressorsWithMultipleLinks ?? 0}
                    width="100%"
                    height={80}
                    dashClassName="min-w-0"
                    pClassName="leading-[1.15]!"
                />
            </section>
        </article>
    )
}
